import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Search, Trash2, Eye, EyeOff, Mail, Phone,
  Building2, MapPin, Package, MessageSquare, FileText, Loader2, Inbox,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_agglos-producer/artifacts/zs2rc3fd_10.png";

const STATUS_LABEL = {
  new: { label: "Nouveau", className: "bg-[#F5A623] text-[#1E3A5F] hover:bg-[#F5A623]" },
  in_progress: { label: "En cours", className: "bg-blue-500 text-white hover:bg-blue-500" },
  done: { label: "Traité", className: "bg-emerald-500 text-white hover:bg-emerald-500" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.new;
  return <Badge className={s.className} data-testid={`status-badge-${status || "new"}`}>{s.label}</Badge>;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ unread_quotes: 0, unread_contacts: 0, total_quotes: 0, total_contacts: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status_filter: statusFilter, search: search || undefined };
      const [q, c, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers, params }),
        axios.get(`${API}/admin/contacts`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter, search, logout, navigate]);

  useEffect(() => { if (token) fetchAll(); }, [fetchAll, token]);

  const updateItem = async (kind, id, patch) => {
    const url = `${API}/admin/${kind}/${id}`;
    try {
      await axios.patch(url, patch, { headers });
      await fetchAll();
    } catch { toast.error("Impossible de mettre à jour"); }
  };

  const deleteItem = async (kind, id) => {
    try {
      await axios.delete(`${API}/admin/${kind}/${id}`, { headers });
      toast.success("Supprimé");
      await fetchAll();
    } catch { toast.error("Suppression impossible"); }
  };

  const toggleExpand = (item, kind) => {
    const newId = expandedId === item.id ? null : item.id;
    setExpandedId(newId);
    if (newId && !item.read) updateItem(kind, item.id, { read: true });
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
    toast.success("Déconnecté");
  };

  const list = tab === "quotes" ? quotes : contacts;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] border-b-4 border-[#F5A623] text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="AGRABAT" className="h-8 md:h-10" />
            <div>
              <h1 className="text-lg md:text-xl font-bold">Tableau de bord</h1>
              <p className="text-xs text-slate-300 hidden md:block">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card className="border-l-4 border-l-[#F5A623]">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase font-semibold">Devis non lus</div>
              <div className="text-3xl font-bold text-[#1E3A5F]" data-testid="stat-unread-quotes">{stats.unread_quotes}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase font-semibold">Total devis</div>
              <div className="text-3xl font-bold text-slate-700" data-testid="stat-total-quotes">{stats.total_quotes}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-[#F5A623]">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase font-semibold">Messages non lus</div>
              <div className="text-3xl font-bold text-[#1E3A5F]" data-testid="stat-unread-contacts">{stats.unread_contacts}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase font-semibold">Total messages</div>
              <div className="text-3xl font-bold text-slate-700" data-testid="stat-total-contacts">{stats.total_contacts}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setExpandedId(null); }}>
          <TabsList className="w-full md:w-auto grid grid-cols-2 md:inline-flex">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="gap-2">
              <FileText className="w-4 h-4" />
              Devis
              {stats.unread_quotes > 0 && (
                <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {stats.unread_quotes}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
              {stats.unread_contacts > 0 && (
                <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {stats.unread_contacts}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="mt-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher (nom, email, téléphone...)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-52" data-testid="admin-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveau</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="done">Traité</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <TabsContent value="quotes" className="mt-4">
            <ItemList
              items={list}
              kind="quotes"
              loading={loading}
              expandedId={expandedId}
              onExpand={(item) => toggleExpand(item, "quotes")}
              onUpdateStatus={(id, s) => updateItem("quotes", id, { status: s })}
              onToggleRead={(item) => updateItem("quotes", item.id, { read: !item.read })}
              onDelete={(id) => deleteItem("quotes", id)}
              renderPrimary={(q) => (
                <div>
                  <div className="font-semibold text-[#1E3A5F]">{q.full_name}</div>
                  <div className="text-xs text-slate-500">{q.product_type} — {q.quantity}</div>
                </div>
              )}
              renderDetails={(q) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Field icon={Mail} label="Email" value={<a href={`mailto:${q.email}`} className="text-[#1E3A5F] hover:underline">{q.email}</a>} />
                  <Field icon={Phone} label="Téléphone" value={<a href={`tel:${q.phone}`} className="text-[#1E3A5F] hover:underline">{q.phone}</a>} />
                  {q.company && <Field icon={Building2} label="Société" value={q.company} />}
                  <Field icon={Package} label="Produit" value={`${q.product_type} — ${q.quantity}`} />
                  <Field icon={MapPin} label="Livraison" value={q.delivery_address} className="md:col-span-2" />
                  {q.message && <Field icon={MessageSquare} label="Message" value={<p className="whitespace-pre-wrap">{q.message}</p>} className="md:col-span-2" />}
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <ItemList
              items={list}
              kind="contacts"
              loading={loading}
              expandedId={expandedId}
              onExpand={(item) => toggleExpand(item, "contacts")}
              onUpdateStatus={(id, s) => updateItem("contacts", id, { status: s })}
              onToggleRead={(item) => updateItem("contacts", item.id, { read: !item.read })}
              onDelete={(id) => deleteItem("contacts", id)}
              renderPrimary={(m) => (
                <div>
                  <div className="font-semibold text-[#1E3A5F]">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.subject}</div>
                </div>
              )}
              renderDetails={(m) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Field icon={Mail} label="Email" value={<a href={`mailto:${m.email}`} className="text-[#1E3A5F] hover:underline">{m.email}</a>} />
                  {m.phone && <Field icon={Phone} label="Téléphone" value={<a href={`tel:${m.phone}`} className="text-[#1E3A5F] hover:underline">{m.phone}</a>} />}
                  <Field icon={FileText} label="Sujet" value={m.subject} className="md:col-span-2" />
                  <Field icon={MessageSquare} label="Message" value={<p className="whitespace-pre-wrap">{m.message}</p>} className="md:col-span-2" />
                </div>
              )}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={`flex gap-2 ${className}`}>
      <Icon className="w-4 h-4 text-[#F5A623] flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase text-slate-400 font-semibold">{label}</div>
        <div className="text-slate-700 break-words">{value}</div>
      </div>
    </div>
  );
}

function ItemList({ items, kind, loading, expandedId, onExpand, onUpdateStatus, onToggleRead, onDelete, renderPrimary, renderDetails }) {
  if (loading && items.length === 0) {
    return (
      <div className="text-center py-16" data-testid={`${kind}-loading`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F] mx-auto" />
      </div>
    );
  }
  if (!loading && items.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center" data-testid={`${kind}-empty`}>
          <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Aucun élément à afficher</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid={`${kind}-list`}>
      {items.map((item) => (
        <Card
          key={item.id}
          className={`transition-all ${!item.read ? "border-l-4 border-l-[#F5A623] bg-white" : "bg-white"}`}
          data-testid={`${kind}-item-${item.id}`}
        >
          <CardHeader className="p-4 cursor-pointer" onClick={() => onExpand(item)}>
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex-1 min-w-0">{renderPrimary(item)}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                <StatusBadge status={item.status} />
                {!item.read && (
                  <Badge className="bg-red-500 text-white hover:bg-red-500" data-testid={`unread-badge-${item.id}`}>
                    Non lu
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          {expandedId === item.id && (
            <CardContent className="border-t pt-4" data-testid={`${kind}-details-${item.id}`}>
              {renderDetails(item)}
              <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t">
                <Select value={item.status || "new"} onValueChange={(s) => onUpdateStatus(item.id, s)}>
                  <SelectTrigger className="w-40" data-testid={`change-status-${item.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="done">Traité</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onToggleRead(item); }}
                  data-testid={`toggle-read-${item.id}`}
                >
                  {item.read ? <><EyeOff className="w-4 h-4 mr-2" /> Marquer non lu</> : <><Eye className="w-4 h-4 mr-2" /> Marquer lu</>}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-btn-${item.id}`}>
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                      <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(item.id)}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid={`confirm-delete-${item.id}`}
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
